import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import BulkEditPanel from './BulkEditPanel';
import ColumnDetailPanel from './ColumnDetailPanel';
import ColumnList from './ColumnList';
import DictionaryTree from './DictionaryTree';
import { PAGE_SIZE, buildSearchParams, dataDictionaryApi } from './dataDictionaryApi';
import { errorMessage, formatNumber, rowKey } from './dataDictionaryUtils';

/** Must match MAX_BULK_ROWS in backend/app/api/data_dictionary.py. */
export const BULK_MAX = 500;

export const DEFAULT_FILTERS = { q: '', visible: null, complete: null, tag: '', missing: '' };
export const DEFAULT_BROWSE_STATE = {
  mode: 'folders',
  scope: { type: 'all' },
  filters: DEFAULT_FILTERS,
  sortBy: 'display_name',
  sortOrder: 'asc',
  offset: 0,
};

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function FilterMenu({ label, value, options, onChange, active }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const current = options.find((o) => o.value === value) || options[0];
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 text-[13px] transition-colors ${
          active ? 'border-primary-200 bg-primary-50 text-primary-700' : 'border-hairline bg-surface text-[#18181B] hover:border-zinc-300'
        }`}
      >
        <span className={active ? '' : 'text-[#71717A]'}>{label}</span>
        <b className="font-semibold">{current.label}</b>
        <ChevronDown size={13} className="text-zinc-500" aria-hidden />
      </button>
      {open ? (
        <div role="listbox" className="absolute left-0 z-30 mt-1 min-w-[10rem] rounded-xl border border-hairline bg-white p-1 shadow-[0_12px_32px_rgba(24,24,27,0.12),0_2px_6px_rgba(24,24,27,0.06)]">
          {options.map((o) => (
            <button
              key={String(o.value)}
              type="button"
              role="option"
              aria-selected={o.value === value}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={`flex h-8 w-full items-center rounded-md px-2.5 text-left text-[13px] hover:bg-[#F3F2EE] ${o.value === value ? 'font-semibold text-[#18181B]' : 'text-[#3F3F46]'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function scopeLabel(scope) {
  if (!scope || scope.type === 'all') return 'all columns';
  if (scope.type === 'folder') return scope.path.join(' / ');
  if (scope.type === 'nofolder') return 'No folder';
  if (scope.type === 'schema') return scope.schema;
  if (scope.type === 'table') return `${scope.schema}.${scope.table}`;
  return '';
}

/**
 * Browse tab: tree (left) + filtered column list (center) + detail / bulk panel (right).
 * `state` / `onStateChange` are lifted so Overview deep-links can drive this view.
 */
export default function BrowseView({ state, onStateChange, meta, onEdited, publishPending }) {
  const { mode, scope, filters, sortBy, sortOrder, offset } = state;
  const patch = useCallback((p) => onStateChange((prev) => ({ ...prev, ...p })), [onStateChange]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedKey, setSelectedKey] = useState(null);
  const [checked, setChecked] = useState(() => new Map()); // key -> row
  const [panelOpen, setPanelOpen] = useState(true);
  const genRef = useRef(0);
  const dirtyRef = useRef(false);
  const lastCheckedRef = useRef(null);

  const debouncedQ = useDebounced(filters.q, 300);
  const effectiveFilters = useMemo(() => ({ ...filters, q: debouncedQ }), [filters, debouncedQ]);

  const fetchPage = useCallback(
    async (quiet = false) => {
      const gen = ++genRef.current;
      if (!quiet) setLoading(true);
      setError('');
      try {
        const data = await dataDictionaryApi.search(
          buildSearchParams({ scope, filters: effectiveFilters, sortBy, sortOrder, offset, limit: PAGE_SIZE })
        );
        if (gen !== genRef.current) return;
        setRows(Array.isArray(data?.hits) ? data.hits : []);
        setTotal(Number(data?.total || 0));
      } catch (e) {
        if (gen !== genRef.current) return;
        setRows([]);
        setTotal(0);
        setError(errorMessage(e, 'Search failed'));
      } finally {
        if (gen === genRef.current) setLoading(false);
      }
    },
    [scope, effectiveFilters, sortBy, sortOrder, offset]
  );

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Reset paging when the query changes.
  const scopeKey = JSON.stringify([scope, effectiveFilters, sortBy, sortOrder]);
  const prevScopeKey = useRef(scopeKey);
  useEffect(() => {
    if (prevScopeKey.current !== scopeKey) {
      prevScopeKey.current = scopeKey;
      if (offset !== 0) patch({ offset: 0 });
    }
  }, [scopeKey, offset, patch]);

  // Keep selected row valid.
  const selectedRow = useMemo(() => rows.find((r) => rowKey(r) === selectedKey) || null, [rows, selectedKey]);
  const selectedIndex = selectedRow ? rows.indexOf(selectedRow) : -1;

  const confirmDiscard = () => {
    if (!dirtyRef.current) return true;
    // eslint-disable-next-line no-alert
    return window.confirm('You have unsaved changes on this column. Discard them?');
  };

  const selectRow = (row) => {
    if (!row) return;
    if (rowKey(row) === selectedKey) {
      setPanelOpen(true);
      return;
    }
    if (!confirmDiscard()) return;
    dirtyRef.current = false;
    setSelectedKey(rowKey(row));
    setPanelOpen(true);
  };

  const moveSelection = (delta) => {
    if (!rows.length) return;
    const idx = selectedIndex < 0 ? 0 : Math.min(rows.length - 1, Math.max(0, selectedIndex + delta));
    selectRow(rows[idx]);
  };

  const toggleChecked = (row, next, e) => {
    setChecked((prev) => {
      const map = new Map(prev);
      const k = rowKey(row);
      // shift-click selects a range on the current page
      if (e?.shiftKey && lastCheckedRef.current) {
        const a = rows.findIndex((r) => rowKey(r) === lastCheckedRef.current);
        const b = rows.findIndex((r) => rowKey(r) === k);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          rows.slice(lo, hi + 1).forEach((r) => (next ? map.set(rowKey(r), r) : map.delete(rowKey(r))));
        }
      }
      if (next) map.set(k, row);
      else map.delete(k);
      lastCheckedRef.current = k;
      return map;
    });
  };

  const toggleAllOnPage = (next) =>
    setChecked((prev) => {
      const map = new Map(prev);
      rows.forEach((r) => (next ? map.set(rowKey(r), r) : map.delete(rowKey(r))));
      return map;
    });

  const selectAllMatching = async () => {
    // Pull every matching key (capped at BULK_MAX) so bulk actions can span pages.
    const cap = BULK_MAX;
    const collected = new Map(checked);
    let off = 0;
    try {
      setLoading(true);
      while (off < Math.min(total, cap)) {
        // eslint-disable-next-line no-await-in-loop
        const data = await dataDictionaryApi.search(
          buildSearchParams({ scope, filters: effectiveFilters, sortBy, sortOrder, offset: off, limit: 200 })
        );
        const hits = Array.isArray(data?.hits) ? data.hits : [];
        hits.forEach((r) => collected.set(rowKey(r), r));
        if (hits.length < 200) break;
        off += 200;
      }
      setChecked(collected);
    } catch (e) {
      setError(errorMessage(e, 'Could not select all matching columns'));
    } finally {
      setLoading(false);
    }
  };

  const mergeRows = (updated) => {
    const byKey = new Map(updated.map((u) => [rowKey(u), u]));
    setRows((prev) => prev.map((r) => (byKey.has(rowKey(r)) ? { ...r, ...byKey.get(rowKey(r)) } : r)));
    setChecked((prev) => {
      if (!prev.size) return prev;
      const map = new Map(prev);
      byKey.forEach((u, k) => {
        if (map.has(k)) map.set(k, { ...map.get(k), ...u });
      });
      return map;
    });
  };

  const handleSaved = (updatedRow, changes) => {
    dirtyRef.current = false;
    mergeRows([updatedRow]);
    onEdited?.({ rows: [updatedRow], changes });
  };

  const handleBulkApplied = (updatedRows, changes) => {
    mergeRows(updatedRows);
    setChecked(new Map());
    onEdited?.({ rows: updatedRows, changes });
    // folder/visibility moves may push rows out of the current scope
    if (changes.folder_list !== undefined || changes.visible !== undefined) fetchPage(true);
  };

  // Keyboard: ↑/↓ move selection, "/" focuses search, Esc clears selection — only outside inputs.
  const searchRef = useRef(null);
  const rootRef = useRef(null);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const onKey = (e) => {
      const t = e.target;
      const inField = t instanceof Element && t.closest('input, textarea, select, [contenteditable="true"], [role="listbox"], [role="tree"]');
      if (e.key === '/' && !inField) {
        e.preventDefault();
        searchRef.current?.focus();
      } else if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !inField) {
        e.preventDefault();
        moveSelection(e.key === 'ArrowDown' ? 1 : -1);
      } else if (e.key === 'Escape' && !inField) {
        if (checked.size) setChecked(new Map());
      }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, selectedIndex, checked.size]);

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total ? offset + 1 : 0;
  const to = Math.min(total, offset + rows.length);
  const activeFilterCount = [filters.visible != null, filters.complete != null, Boolean(filters.tag), Boolean(filters.missing)].filter(Boolean).length;
  const checkedRows = useMemo(() => Array.from(checked.values()), [checked]);
  const showTableCol = !scope || scope.type === 'all' || scope.type === 'folder' || scope.type === 'nofolder' || scope.type === 'schema';
  const showRightPanel = checkedRows.length > 0 || (panelOpen && selectedRow);

  return (
    <div ref={rootRef} className="flex min-h-0 flex-1 overflow-hidden rounded-card border border-hairline bg-surface shadow-card" tabIndex={-1}>
      <aside className="w-[236px] shrink-0 border-r border-hairline">
        <DictionaryTree
          mode={mode}
          onModeChange={(m) => patch({ mode: m, scope: { type: 'all' } })}
          folders={meta.folders}
          noFolder={meta.noFolder}
          tables={meta.stats?.tables || []}
          totalColumns={meta.stats?.overall?.total}
          scope={scope}
          onScopeChange={(s) => patch({ scope: s, offset: 0 })}
        />
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-4 py-3">
          <label className="relative block w-[220px]">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" aria-hidden />
            <input
              ref={searchRef}
              type="search"
              value={filters.q}
              onChange={(e) => patch({ filters: { ...filters, q: e.target.value } })}
              placeholder="Search columns…"
              aria-label="Search columns"
              className="h-8 w-full rounded-md border border-hairline bg-surface pl-8 pr-8 text-[13px] placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#18181B]/10"
            />
            {filters.q ? (
              <button type="button" onClick={() => patch({ filters: { ...filters, q: '' } })} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700" aria-label="Clear search">
                <X size={13} aria-hidden />
              </button>
            ) : (
              <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-hairline px-1 text-[10px] text-zinc-400">/</kbd>
            )}
          </label>
          <FilterMenu
            label="Visible"
            value={filters.visible}
            active={filters.visible != null}
            onChange={(v) => patch({ filters: { ...filters, visible: v } })}
            options={[
              { value: null, label: 'Any' },
              { value: true, label: 'Visible' },
              { value: false, label: 'Hidden' },
            ]}
          />
          <FilterMenu
            label="Done"
            value={filters.complete}
            active={filters.complete != null}
            onChange={(v) => patch({ filters: { ...filters, complete: v } })}
            options={[
              { value: null, label: 'Any' },
              { value: true, label: 'Complete' },
              { value: false, label: 'Incomplete' },
            ]}
          />
          <FilterMenu
            label="Missing"
            value={filters.missing}
            active={Boolean(filters.missing)}
            onChange={(v) => patch({ filters: { ...filters, missing: v } })}
            options={[
              { value: '', label: 'Nothing' },
              { value: 'description', label: 'Description' },
              { value: 'folder', label: 'Folder' },
              { value: 'tags', label: 'Tags' },
              { value: 'display_name', label: 'Display name' },
            ]}
          />
          <FilterMenu
            label="Tag"
            value={filters.tag}
            active={Boolean(filters.tag)}
            onChange={(v) => patch({ filters: { ...filters, tag: v } })}
            options={[{ value: '', label: 'Any' }, ...meta.tags.slice(0, 40).map((t) => ({ value: t.tag, label: `${t.tag} (${formatNumber(t.count)})` }))]}
          />
          {activeFilterCount || filters.q ? (
            <button type="button" onClick={() => patch({ filters: DEFAULT_FILTERS })} className="h-8 rounded-md px-2 text-xs text-[#71717A] hover:bg-[#F3F2EE] hover:text-[#18181B]">
              Reset
            </button>
          ) : null}
          <span className="ml-auto whitespace-nowrap text-xs text-[#71717A]">
            <b className="font-semibold text-[#18181B]">{formatNumber(total)}</b> column{total === 1 ? '' : 's'} in {scopeLabel(scope)}
          </span>
        </div>

        {checkedRows.length > 0 ? (
          <div className="flex items-center gap-2 whitespace-nowrap border-b border-primary-100 bg-primary-50 px-4 py-2 text-[13px] text-primary-700">
            <b className="font-semibold">{formatNumber(checkedRows.length)} selected</b>
            {total > checkedRows.length ? (
              <>
                <span>·</span>
                <button type="button" onClick={selectAllMatching} className="hover:underline">
                  Select {total > BULK_MAX ? `first ${formatNumber(BULK_MAX)}` : `all ${formatNumber(total)}`} in {scopeLabel(scope)}
                </button>
              </>
            ) : null}
            <button type="button" onClick={() => setChecked(new Map())} className="ml-auto hover:underline">
              Clear selection
            </button>
          </div>
        ) : null}

        <ColumnList
          rows={rows}
          loading={loading}
          error={error}
          selectedKey={selectedKey}
          onSelectRow={selectRow}
          checkedKeys={checked}
          onToggleChecked={toggleChecked}
          onToggleAllOnPage={toggleAllOnPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={(field) =>
            patch(sortBy === field ? { sortOrder: sortOrder === 'asc' ? 'desc' : 'asc' } : { sortBy: field, sortOrder: 'asc' })
          }
          showTable={showTableCol}
        />

        <div className="flex items-center justify-between gap-3 border-t border-hairline px-4 py-2 text-xs text-[#71717A]">
          <span className="whitespace-nowrap">
            {total ? `${formatNumber(from)}–${formatNumber(to)} of ${formatNumber(total)}` : 'No results'} ·{' '}
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle" aria-hidden /> complete{' '}
            <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-amber-500 align-middle" aria-hidden /> incomplete
            {publishPending ? <span className="ml-3 text-amber-700">{formatNumber(publishPending)} unpublished</span> : null}
          </span>
          <div className="flex items-center gap-1">
            <button type="button" disabled={page <= 1 || loading} onClick={() => patch({ offset: Math.max(0, offset - PAGE_SIZE) })} className="inline-flex h-7 items-center gap-1 rounded-md border border-hairline bg-surface px-2 text-xs text-[#18181B] hover:bg-[#F3F2EE] disabled:opacity-40" aria-label="Previous page">
              <ChevronLeft size={13} aria-hidden /> Prev
            </button>
            <span className="px-2 tabular-nums">
              Page {page} of {pages}
            </span>
            <button type="button" disabled={page >= pages || loading} onClick={() => patch({ offset: offset + PAGE_SIZE })} className="inline-flex h-7 items-center gap-1 rounded-md border border-hairline bg-surface px-2 text-xs text-[#18181B] hover:bg-[#F3F2EE] disabled:opacity-40" aria-label="Next page">
              Next <ChevronRight size={13} aria-hidden />
            </button>
          </div>
        </div>
      </section>

      {showRightPanel ? (
        <aside className="w-[344px] shrink-0 border-l border-hairline">
          {checkedRows.length > 0 ? (
            <BulkEditPanel rows={checkedRows} onClear={() => setChecked(new Map())} onApplied={handleBulkApplied} folders={meta.folders} tagOptions={meta.tags} />
          ) : (
            <ColumnDetailPanel
              key={selectedKey}
              row={selectedRow}
              position={offset + selectedIndex + 1}
              total={total}
              onClose={() => {
                if (!confirmDiscard()) return;
                dirtyRef.current = false;
                setPanelOpen(false);
              }}
              onPrev={selectedIndex > 0 ? () => moveSelection(-1) : null}
              onNext={selectedIndex < rows.length - 1 ? () => moveSelection(1) : null}
              onSaved={handleSaved}
              folders={meta.folders}
              tagOptions={meta.tags}
              dirtyRef={dirtyRef}
              workspaceUrl={meta.stats?.workspace_url}
            />
          )}
        </aside>
      ) : null}
    </div>
  );
}
